'use client';

/**
 * ./src/modules/shopping-list/components/ShoppingListGrid.tsx
 *
 * ShoppingListGrid component
 *
 * @copyright DesignShop, 2024
 * @since 08/13/2024
 */
import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { VISUALIZER_ENABLED } from '@/modules/common/lib/environment';
import { CollectionCard } from '@/modules/product/components/collections/CollectionCard/CollectionCard';
import { SampleHitsSkeleton } from '@/modules/product/components/samples/SamplesHits';
import { SampleCard } from '@/modules/sample-card';
import SavedLookCard from '@/modules/visualizer/components/v1/SavedLookCard';

import { useShoppingList } from '../context/ShoppingListContext';
import { selectCurrentFilter } from '../state/shoppingListSlice';

export const ShoppingListGrid = () => {
  const { mixedShoppingListItems, isLoading } = useShoppingList();
  const shoppingListInitialLoading = useAppSelector((state) => state.shoppingList.initialLoading);
  const currentFilter = useAppSelector(selectCurrentFilter);

  const visualizerAwareItems =
    VISUALIZER_ENABLED ? mixedShoppingListItems : (
      mixedShoppingListItems.filter((item) => item.type !== 'look')
    );

  const filteredMixedShoppingListItems =
    currentFilter === 'all' ? visualizerAwareItems : (
      visualizerAwareItems.filter((item) => item.type === currentFilter)
    );

  if (isLoading || shoppingListInitialLoading) {
    return (
      <div className="grid h-fit grid-cols-2 gap-2 md:grid-cols-[repeat(auto-fill,minmax(185px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(262px,1fr))]">
        <SampleHitsSkeleton />
      </div>
    );
  }

  return (
    <div className="grid h-fit grid-cols-2 gap-2 md:grid-cols-[repeat(auto-fill,minmax(185px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(262px,1fr))]">
      {filteredMixedShoppingListItems.map((shoppingListItem, index) => {
        if (shoppingListItem.type === 'product' && shoppingListItem.item !== null) {
          const item = shoppingListItem.item;
          const variant = item.variant;

          return (
            <SampleCard
              brandName={item.brandName}
              category={item.category}
              className="w-full"
              collectionName={item.collectionName}
              color={item.imgColor}
              ecommName={item.ecommName}
              id={item.id}
              images={item.images as []}
              index={index}
              isOnStock={item.isOnStock ?? false}
              key={`${variant.sku}-${item.id}-${item.name}`}
              mainCategory={item.mainCategory}
              name={item.name}
              pricePerUnit={item.price}
              published={item.published}
              samplePrice={item.price}
              sampleFormat={item.sampleFormat}
              showProductType={false}
              sku={variant.sku}
              subCategory={item.subCategory}
              type={item.type}
              variants={item?.variants || []}
              where={DataLayerWhere.List}
            />
          );
        } else if (shoppingListItem.type === 'collection') {
          const { title, slug, description, image, products, collectionType } =
            shoppingListItem.item;
          return (
            <CollectionCard
              key={shoppingListItem.item.id}
              title={title}
              isAffiliate={collectionType === 'affiliate'}
              slug={slug}
              description={description}
              image={image}
              products={products}
              className="!max-h-full !max-w-full"
              classNameImg="rounded-none !w-full !h-full aspect-square"
              where={DataLayerWhere.List}
              designerId={shoppingListItem.item.designerId}
              variant="plp"
            />
          );
        } else if (shoppingListItem.type == 'look') {
          return <SavedLookCard key={shoppingListItem.item.id} look={shoppingListItem.item} />;
        }
        return null;
      })}
    </div>
  );
};
